import React, {useEffect, useState} from 'react';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Icon from '../../components/ui/Icon';
import {api} from '../../lib/api';
import {sampleLesson} from '../../data/mock';
import styles from './AdminCourses.module.scss';

const emptyStep = {title: '', description: '', blockHint: '', editorTip: ''};
const formatJson = value => JSON.stringify(value ?? {}, null, 2);
const defaultGroundTruth = {
    summary: '描述这个关卡完成后应该达成的作品效果。',
    checks: [
        {id: 'green_flag', label: '使用绿旗事件启动程序', type: 'block', opcode: 'event_whenflagclicked', minCount: 1}
    ],
    aiPrompt: '判断作品是否达成关卡目标，并用适合小学生的语气给出反馈。'
};

const createBlankLesson = id => ({
    id,
    title: '新关卡',
    intro: '写一句适合小朋友理解的关卡介绍。',
    starter: {
        moduleName: 'Scratch 基础',
        sprite: 'Scratch 小猫',
        backdrop: '空白舞台',
        sounds: '喵叫声',
        extensions: ''
    },
    groundTruth: defaultGroundTruth,
    steps: []
});

export default function AdminCourses () {
    const [lessonId, setLessonId] = useState('loops');
    const [lessonIdInput, setLessonIdInput] = useState('loops');
    const [lessonList, setLessonList] = useState([]);
    const [lesson, setLesson] = useState(sampleLesson);
    const [groundTruthText, setGroundTruthText] = useState(formatJson(sampleLesson.groundTruth));
    const [draft, setDraft] = useState(emptyStep);
    const [status, setStatus] = useState('');

    const loadLesson = async id => {
        setStatus('正在加载课程…');
        try {
            const res = await api.getLesson(id);
            setLesson(res.lesson);
            setLessonIdInput(id);
            setGroundTruthText(formatJson(res.lesson.groundTruth));
            setStatus('已从后端加载');
        } catch (err) {
            const draftLesson = createBlankLesson(id);
            setLesson(draftLesson);
            setLessonIdInput(id);
            setGroundTruthText(formatJson(defaultGroundTruth));
            setStatus(`未找到课程，已准备新关卡草稿：${err.message}`);
        }
    };

    const loadLessonList = async () => {
        try {
            const res = await api.listLessons();
            setLessonList(res.lessons ?? []);
        } catch (err) {
            setStatus(`课程列表加载失败：${err.message}`);
        }
    };

    useEffect(() => {
        loadLesson(lessonId);
    }, [lessonId]);

    useEffect(() => {
        loadLessonList();
    }, []);

    const saveLesson = async () => {
        let groundTruth;
        try {
            groundTruth = JSON.parse(groundTruthText || '{}');
        } catch {
            setStatus('验收标准 JSON 格式不正确');
            return;
        }
        await api.saveLesson(lesson.id, {title: lesson.title, intro: lesson.intro, starter: lesson.starter, groundTruth});
        await loadLessonList();
        setStatus('课程信息已保存');
    };

    const updateStep = async step => {
        await api.updateLessonStep(step.id, {
            title: step.title,
            description: step.desc,
            blockHint: step.blockHint,
            editorTip: step.editorTip,
            stepOrder: step.n
        });
        setStatus(`步骤 ${step.n} 已保存`);
    };

    const addStep = async () => {
        if (!draft.title || !draft.description) {
            setStatus('请填写新步骤标题和说明');
            return;
        }
        await api.addLessonStep(lesson.id, draft);
        setDraft(emptyStep);
        await loadLesson(lesson.id);
        setStatus('新步骤已添加');
    };

    const startNewLesson = () => {
        const nextId = lessonIdInput.trim() || `quest-${Date.now().toString(36)}`;
        const draftLesson = createBlankLesson(nextId);
        setLessonIdInput(nextId);
        setLesson(draftLesson);
        setDraft(emptyStep);
        setGroundTruthText(formatJson(defaultGroundTruth));
        setStatus('已创建新关卡草稿，填写后先保存课程信息，再添加步骤');
    };

    const patchStep = (id, patch) => {
        setLesson(prev => ({
            ...prev,
            steps: prev.steps.map(s => (s.id === id ? {...s, ...patch} : s))
        }));
    };

    return (
        <div className={`cq-container ${styles.page}`}>
            <div className={styles.head}>
                <div>
                    <h1 className={styles.title}>课程后台</h1>
                    <p className={styles.sub}>维护课程说明、步骤文案和编辑器内提示。</p>
                </div>
                <Button to={`/lab/${lesson.id}`} variant="secondary" iconRight={<Icon name="arrowRight" size={18} />}>
                    预览课程
                </Button>
            </div>

            <div className={styles.adminGrid}>
                <Card className={styles.coursePicker}>
                    <div className={styles.pickerHead}>
                        <h2 className={styles.sectionTitle}>选择课程</h2>
                        <button type="button" className={styles.refreshBtn} onClick={loadLessonList}>刷新</button>
                    </div>
                    <button type="button" className={styles.newLessonBtn} onClick={startNewLesson}>
                        <Icon name="plus" size={18} /> 新建关卡
                    </button>
                    <div className={styles.courseList}>
                        {lessonList.map(item => (
                            <button
                                key={item.id}
                                type="button"
                                className={[styles.courseItem, item.id === lesson.id ? styles.courseActive : ''].join(' ')}
                                onClick={() => setLessonId(item.id)}
                            >
                                <span>{item.title}</span>
                                <small>{item.id} · {item.module_name}</small>
                            </button>
                        ))}
                    </div>

                    <label className={styles.label} htmlFor="lesson-id">课程 ID / 新关卡 ID</label>
                    <div className={styles.idRow}>
                        <input
                            id="lesson-id"
                            className={styles.input}
                            value={lessonIdInput}
                            onChange={e => setLessonIdInput(e.target.value)}
                        />
                        <button
                            type="button"
                            className={styles.loadBtn}
                            onClick={() => setLessonId(lessonIdInput.trim() || 'loops')}
                        >
                            加载
                        </button>
                    </div>
                </Card>

                <div className={styles.editorStack}>
                    <Card className={styles.panel}>
                        <label className={styles.label}>课程标题</label>
                        <input
                            className={styles.input}
                            value={lesson.title}
                            onChange={e => setLesson(prev => ({...prev, title: e.target.value}))}
                        />

                        <label className={styles.label}>课程介绍</label>
                        <textarea
                            className={styles.textarea}
                            value={lesson.intro}
                            onChange={e => setLesson(prev => ({...prev, intro: e.target.value}))}
                        />

                        <div className={styles.assetGrid}>
                            <label className={styles.label}>模块</label>
                            <input
                                className={styles.input}
                                value={lesson.starter?.moduleName ?? ''}
                                onChange={e => setLesson(prev => ({
                                    ...prev,
                                    starter: {...prev.starter, moduleName: e.target.value}
                                }))}
                            />
                            <label className={styles.label}>角色/模型</label>
                            <input
                                className={styles.input}
                                value={lesson.starter?.sprite ?? ''}
                                onChange={e => setLesson(prev => ({
                                    ...prev,
                                    starter: {...prev.starter, sprite: e.target.value}
                                }))}
                            />
                            <label className={styles.label}>背景</label>
                            <input
                                className={styles.input}
                                value={lesson.starter?.backdrop ?? ''}
                                onChange={e => setLesson(prev => ({
                                    ...prev,
                                    starter: {...prev.starter, backdrop: e.target.value}
                                }))}
                            />
                            <label className={styles.label}>声音</label>
                            <input
                                className={styles.input}
                                value={lesson.starter?.sounds ?? ''}
                                onChange={e => setLesson(prev => ({
                                    ...prev,
                                    starter: {...prev.starter, sounds: e.target.value}
                                }))}
                            />
                            <label className={styles.label}>推荐扩展</label>
                            <input
                                className={styles.input}
                                placeholder="例如：music,pen"
                                value={lesson.starter?.extensions ?? ''}
                                onChange={e => setLesson(prev => ({
                                    ...prev,
                                    starter: {...prev.starter, extensions: e.target.value}
                                }))}
                            />
                        </div>

                        <label className={styles.label}>验收标准 Ground Truth（JSON）</label>
                        <textarea
                            className={[styles.textarea, styles.codeArea].join(' ')}
                            value={groundTruthText}
                            onChange={e => setGroundTruthText(e.target.value)}
                        />

                        <div className={styles.actions}>
                            <Button type="button" onClick={saveLesson}>保存课程信息</Button>
                            <span className={styles.status}>{status}</span>
                        </div>
                    </Card>

                    <section className={styles.steps}>
                        <h2 className={styles.sectionTitle}>步骤维护</h2>
                        {(lesson.steps ?? []).map(step => (
                            <Card key={step.id ?? step.n} className={styles.stepCard}>
                        <div className={styles.stepTop}>
                            <span className={styles.order}>第 {step.n} 步</span>
                            <Button type="button" size="sm" variant="soft" onClick={() => updateStep(step)}>保存步骤</Button>
                        </div>
                        <input
                            className={styles.input}
                            value={step.title}
                            onChange={e => patchStep(step.id, {title: e.target.value})}
                        />
                        <textarea
                            className={styles.textarea}
                            value={step.desc}
                            onChange={e => patchStep(step.id, {desc: e.target.value})}
                        />
                        <input
                            className={styles.input}
                            placeholder="积木提示，例如：控制：重复 10 次"
                            value={step.blockHint ?? ''}
                            onChange={e => patchStep(step.id, {blockHint: e.target.value})}
                        />
                        <textarea
                            className={styles.textarea}
                            placeholder="显示在编辑器内部的操作提示"
                            value={step.editorTip ?? ''}
                            onChange={e => patchStep(step.id, {editorTip: e.target.value})}
                        />
                            </Card>
                        ))}
                    </section>

                    <Card className={styles.panel}>
                        <h2 className={styles.sectionTitle}>添加新步骤</h2>
                        <input
                            className={styles.input}
                            placeholder="步骤标题"
                            value={draft.title}
                            onChange={e => setDraft(prev => ({...prev, title: e.target.value}))}
                        />
                        <textarea
                            className={styles.textarea}
                            placeholder="步骤说明"
                            value={draft.description}
                            onChange={e => setDraft(prev => ({...prev, description: e.target.value}))}
                        />
                        <input
                            className={styles.input}
                            placeholder="积木提示"
                            value={draft.blockHint}
                            onChange={e => setDraft(prev => ({...prev, blockHint: e.target.value}))}
                        />
                        <textarea
                            className={styles.textarea}
                            placeholder="编辑器内部提示"
                            value={draft.editorTip}
                            onChange={e => setDraft(prev => ({...prev, editorTip: e.target.value}))}
                        />
                        <Button type="button" onClick={addStep} iconLeft={<Icon name="plus" size={18} />}>添加步骤</Button>
                    </Card>
                </div>
            </div>
        </div>
    );
}
